// src/pages/notification/NotificationManagement.js
// Gestión de notificaciones WhatsApp enviadas al cambiar el estado de citas de grooming

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Alert,
  Button,
  Spinner,
  Modal,
  Form,
  Card,
  Table,
  Badge,
} from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import {
  getNotificationList,
  getNotificationById,
  retryNotification,
} from '../../services/notificationApi';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'ENVIANDO', label: 'Enviando' },
  { value: 'ENVIADO', label: 'Enviado' },
  { value: 'FALLIDO', label: 'Fallido' },
];

const STATUS_BADGE = {
  PENDIENTE: 'warning',
  ENVIANDO: 'info',
  ENVIADO: 'success',
  FALLIDO: 'danger',
};

const PAGE_SIZE = 20;

const NotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [meta, setMeta] = useState({ limit: PAGE_SIZE, offset: 0 });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [filters, setFilters] = useState({
    status: '',
    from: '',
    to: '',
  });
  const [currentPage, setCurrentPage] = useState(0);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailNotification, setDetailNotification] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      const offset = currentPage * PAGE_SIZE;
      const { ok, status, data } = await getNotificationList({
        status: filters.status || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        limit: PAGE_SIZE,
        offset,
      });

      if (ok && (status === 200 || data?.code === 200)) {
        const raw = data?.data ?? data?.notifications ?? data?.message ?? data;
        const list = Array.isArray(raw) ? raw : [];
        setNotifications(list);
        setMeta({
          limit: data.meta?.limit ?? PAGE_SIZE,
          offset: data.meta?.offset ?? offset,
          total: data.meta?.total ?? list.length,
        });
      } else {
        setErrorMsg(data?.message || `Error cargando notificaciones (status: ${status})`);
      }
    } catch (err) {
      console.error(err);
      setErrorMsg('Error inesperado al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.status, filters.from, filters.to]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(0);
  };

  const handleApplyFilters = () => {
    setCurrentPage(0);
    loadList();
  };

  const handleClearFilters = () => {
    setFilters({ status: '', from: '', to: '' });
    setCurrentPage(0);
  };

  const openDetail = async (id) => {
    setShowDetailModal(true);
    setDetailNotification(null);
    setDetailLogs([]);
    setDetailLoading(true);
    try {
      const { ok, status, data } = await getNotificationById(id);
      if (ok && (status === 200 || data?.code === 200)) {
        const notif = data.data ?? data.notification ?? data;
        setDetailNotification(notif);
        setDetailLogs(notif.logs ?? []);
      } else {
        setErrorMsg(data?.message || 'Error al cargar detalle');
      }
    } catch (err) {
      setErrorMsg('Error al cargar detalle');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setDetailNotification(null);
    setDetailLogs([]);
  };

  const handleRetry = async (id) => {
    setRetryingId(id);
    setErrorMsg('');
    setSuccessMsg('');
    try {
      const { ok, status, data } = await retryNotification(id);
      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        setSuccessMsg('Notificación marcada para reenvío. El cron la procesará en breve.');
        closeDetail();
        loadList();
      } else {
        setErrorMsg(data?.message || `Error al reintentar (status: ${status})`);
      }
    } catch (err) {
      setErrorMsg('Error al reintentar notificación');
    } finally {
      setRetryingId(null);
    }
  };

  const formatDate = (str) => {
    if (!str) return '—';
    const d = new Date(str);
    return isNaN(d.getTime()) ? str : d.toLocaleString('es-ES');
  };

  const totalPages = Math.max(1, Math.ceil((meta.total ?? 0) / PAGE_SIZE));
  const hasNext = currentPage < totalPages - 1;
  const hasPrev = currentPage > 0;

  return (
    <>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            {errorMsg && (
              <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible className="mb-3">
                {errorMsg}
              </Alert>
            )}
            {successMsg && (
              <Alert variant="success" onClose={() => setSuccessMsg('')} dismissible className="mb-3">
                {successMsg}
              </Alert>
            )}

            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">
                  <span className="me-2">🔔</span>
                  Notificaciones WhatsApp (citas de grooming)
                </h5>
              </Card.Header>
              <Card.Body>
                <p className="text-muted small mb-3">
                  Notificaciones enviadas al cliente cuando se modifica el estado de una cita. Puedes filtrar, ver detalle y reenviar las fallidas.
                </p>

                <Row className="g-2 mb-3">
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label className="small">Estado</Form.Label>
                      <Form.Select
                        value={filters.status}
                        onChange={(e) => handleFilterChange('status', e.target.value)}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value || 'all'} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label className="small">Desde (fecha)</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.from}
                        onChange={(e) => handleFilterChange('from', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group>
                      <Form.Label className="small">Hasta (fecha)</Form.Label>
                      <Form.Control
                        type="date"
                        value={filters.to}
                        onChange={(e) => handleFilterChange('to', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4} className="d-flex align-items-end gap-2">
                    <Button variant="primary" size="sm" onClick={handleApplyFilters}>
                      Buscar
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                      Limpiar
                    </Button>
                  </Col>
                </Row>

                {loading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    <Table responsive bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Destinatario</th>
                          <th>Plantilla</th>
                          <th>Estado</th>
                          <th>Intentos</th>
                          <th>Creado</th>
                          <th>Último error</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {notifications.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">
                              No hay notificaciones con los filtros aplicados.
                            </td>
                          </tr>
                        ) : (
                          notifications.map((n) => (
                            <tr key={n.id_not_queue}>
                              <td>{n.id_not_queue}</td>
                              <td>{n.recipient ?? '—'}</td>
                              <td>{n.template_code ?? '—'}</td>
                              <td>
                                <Badge bg={STATUS_BADGE[n.status_name] || 'secondary'}>
                                  {n.status_name ?? n.id_status ?? '—'}
                                </Badge>
                              </td>
                              <td>{n.attempts ?? 0}</td>
                              <td>{formatDate(n.created_at)}</td>
                              <td className="small text-break" style={{ maxWidth: 200 }}>
                                {n.last_error ? (
                                  <span className="text-danger" title={n.last_error}>
                                    {n.last_error.length > 60 ? `${n.last_error.slice(0, 60)}…` : n.last_error}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => openDetail(n.id_not_queue)}
                                >
                                  Ver
                                </Button>
                                {String(n.status_name || '').toUpperCase() === 'FALLIDO' && (
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleRetry(n.id_not_queue)}
                                    disabled={retryingId === n.id_not_queue}
                                  >
                                    {retryingId === n.id_not_queue ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      'Reenviar'
                                    )}
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          Página {currentPage + 1} de {totalPages} · Mostrando {notifications.length} resultados
                        </small>
                        <div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={!hasPrev}
                            onClick={() => setCurrentPage((p) => p - 1)}
                          >
                            Anterior
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="ms-2"
                            disabled={!hasNext}
                            onClick={() => setCurrentPage((p) => p + 1)}
                          >
                            Siguiente
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal detalle */}
        <Modal show={showDetailModal} onHide={closeDetail} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Detalle de notificación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {detailLoading ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" />
              </div>
            ) : detailNotification ? (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <strong>ID:</strong> {detailNotification.id_not_queue}<br />
                    <strong>Destinatario:</strong> {detailNotification.recipient ?? '—'}<br />
                    <strong>Plantilla:</strong> {detailNotification.template_code ?? '—'}<br />
                    <strong>Estado:</strong>{' '}
                    <Badge bg={STATUS_BADGE[detailNotification.status_name] || 'secondary'}>
                      {detailNotification.status_name ?? '—'}
                    </Badge>
                  </Col>
                  <Col md={6}>
                    <strong>Intentos:</strong> {detailNotification.attempts ?? 0}<br />
                    <strong>Creado:</strong> {formatDate(detailNotification.created_at)}<br />
                    <strong>Actualizado:</strong> {formatDate(detailNotification.updated_at)}<br />
                    {detailNotification.last_error && (
                      <>
                        <strong className="text-danger">Último error:</strong>
                        <div className="small text-danger bg-light p-2 rounded mt-1">
                          {detailNotification.last_error}
                        </div>
                      </>
                    )}
                  </Col>
                </Row>
                {typeof detailNotification.variables === 'object' && detailNotification.variables !== null && (
                  <div className="mb-3">
                    <strong>Variables:</strong>
                    <pre className="bg-light p-2 rounded small mb-0">
                      {JSON.stringify(detailNotification.variables, null, 2)}
                    </pre>
                  </div>
                )}
                {detailLogs.length > 0 && (
                  <div>
                    <strong>Últimos logs:</strong>
                    <Table size="sm" bordered className="mt-2">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Enviado</th>
                          <th>Respuesta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailLogs.map((log, i) => (
                          <tr key={i}>
                            <td>{log.response_code ?? '—'}</td>
                            <td>{formatDate(log.sent_at)}</td>
                            <td className="small text-break" style={{ maxWidth: 300 }}>
                              {typeof log.response_body === 'string'
                                ? log.response_body.slice(0, 120)
                                : JSON.stringify(log.response_body ?? '').slice(0, 120)}
                              {(log.response_body?.length > 120 || (typeof log.response_body === 'object' && log.response_body)) && '…'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
                {detailNotification.status_name === 'FALLIDO' && (
                  <div className="mt-3">
                    <Button
                      variant="warning"
                      onClick={() => handleRetry(detailNotification.id_not_queue)}
                      disabled={retryingId === detailNotification.id_not_queue}
                    >
                      {retryingId === detailNotification.id_not_queue ? (
                        <><Spinner animation="border" size="sm" className="me-2" /> Reenviando…</>
                      ) : (
                        'Reenviar notificación'
                      )}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted">No se pudo cargar el detalle.</p>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </>
  );
};

export default NotificationManagement;
